/**
 * **„Ein Fehler ist aufgetreten" ist keine Fehlermeldung.**
 *
 * Seit AP-28 geteilt (vorher `features/clients/api/fehler.ts`): Artikel und
 * Geschäftspartner melden auf dieselbe Weise, und ein Feature darf nicht quer
 * aus einem anderen importieren.
 *
 * Drei Sorten Inhalt kommen an, und alle müssen durch:
 *
 * - **i18n-Schlüssel** aus dem Dienst (`articles.roleRequired`) — übersetzt; fehlt
 *   die Übersetzung, steht wenigstens der Schlüssel da.
 * - **Sätze von class-validator** — unübersetzt, weil sie den Feldnamen tragen.
 * - **Zahlen neben dem Schlüssel** (`{ message, supplierCount: 2 }`) — sie gehen
 *   als Platzhalter in die Übersetzung, damit die Meldung sagen kann, wie viele.
 */

type Uebersetzer = (key: string, options?: Record<string, unknown>) => string;

/** Sieht das nach einem i18n-Schlüssel aus („clients.roleRequired")? */
function istSchluessel(text: string): boolean {
  return /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9_]+)+$/.test(text) && !text.includes(' ');
}

function uebersetze(text: string, t: Uebersetzer, werte: Record<string, unknown> = {}): string {
  return istSchluessel(text) ? t(text, { defaultValue: text, ...werte }) : text;
}

/**
 * Zahlen und Texte der Antwort als Platzhalter — `supplierCount` wird auch zu `count`.
 * Texte seit AP-10 Nachtrag 5: `fileName` bei abgelehnten Anhängen, `error` beim
 * fehlgeschlagenen Belegversand (dessen Übersetzung ihn schon immer erwartete).
 */
function platzhalter(daten: Record<string, unknown>): Record<string, unknown> {
  const werte: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(daten)) {
    if (k === 'statusCode' || k === 'message') continue;
    if (typeof v === 'string') {
      werte[k] = v;
      continue;
    }
    if (typeof v !== 'number') continue;
    werte[k] = v;
    if (k.endsWith('Count')) werte['count'] = v;
  }
  return werte;
}

/**
 * AP-35 N17-4 — eingefügt oder übernommen, aber vom Modul der Herkunft (Stückliste) abgelehnt: Hinter den Satz kommt
 * je Zeile der Grund („Träger HEA 200: Bestellfreigabe fehlt · Flachstahl: schon bestellt (B2026-0012)"). Die Gründe
 * stehen unter `documents.transfer.reasons.<code>`, mit Angabe unter `<code>_DETAIL`.
 */
function abgelehnteZeilen(daten: Record<string, unknown>, t: Uebersetzer): string {
  if ((daten.code !== 'TRANSFER_REJECTED' && daten.code !== 'PASTE_REJECTED') || !Array.isArray(daten.errors)) return '';
  const sonst = t('documents.transfer.reasons.OTHER', { defaultValue: 'passt nicht' });
  return (daten.errors as { code?: string; label?: string; detail?: string }[])
    .map((f) => {
      const schluessel =
        f.code === 'BOM_APPROVAL_REQUIRED' ? (f.detail === 'INQUIRY' ? `${f.code}_INQUIRY` : f.code) : f.detail ? `${f.code ?? 'OTHER'}_DETAIL` : (f.code ?? 'OTHER');
      const grund = t(`documents.transfer.reasons.${schluessel}`, { detail: f.detail ?? '', defaultValue: sonst });
      return f.label ? `${f.label}: ${grund}` : grund;
    })
    .join(' · ');
}

/** Der Satz, der dem Benutzer angezeigt wird — so genau, wie die Antwort es hergibt. */
export function fehlertext(err: unknown, t: Uebersetzer): string {
  const daten = (err as { response?: { data?: Record<string, unknown> } })?.response?.data ?? {};
  const inhalt = daten.message;

  if (Array.isArray(inhalt)) {
    const saetze = inhalt.filter((e): e is string => typeof e === 'string');
    if (saetze.length) return saetze.map((s) => uebersetze(s, t)).join(' · ');
  }
  if (typeof inhalt === 'string') {
    const satz = uebersetze(inhalt, t, platzhalter(daten));
    const zeilen = abgelehnteZeilen(daten, t);
    return zeilen ? `${satz} ${zeilen}` : satz;
  }
  // Ein Objekt als `message` (Dubletten) hat seinen eigenen Weg in der Maske.
  if (inhalt && typeof inhalt === 'object' && typeof (inhalt as { message?: unknown }).message === 'string') {
    const innen = inhalt as Record<string, unknown>;
    return uebersetze(innen.message as string, t, platzhalter(innen));
  }
  // Ohne Satz vom Server wenigstens sagen, was es war: keine Antwort (Server nicht
  // erreichbar, z. B. während eines Neustarts) oder ein Status ohne Text.
  const antwort = (err as { response?: { status?: number } })?.response;
  if (!antwort) return t('common.errorNoResponse', { defaultValue: 'Der Server hat nicht geantwortet. Bitte gleich noch einmal versuchen.' });
  if (antwort.status) return t('common.errorStatus', { status: antwort.status, defaultValue: `Ein Fehler ist aufgetreten (HTTP ${antwort.status}).` });
  return t('common.error');
}
